import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Clock, Loader2, Send } from "lucide-react";

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  } catch { return ""; }
}

export function CounterProposeDialog({ request, onClose, onSent }) {
  const t = useT();
  const [slots, setSlots] = useState([toLocalInput(request.current_scheduled_at) || ""]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const setSlot = (i, v) => setSlots((prev) => prev.map((s, idx) => (idx === i ? v : s)));
  const addSlot = () => setSlots((prev) => (prev.length >= 3 ? prev : [...prev, ""]));
  const removeSlot = (i) => setSlots((prev) => prev.filter((_, idx) => idx !== i));

  const windows = slots.map((s) => s && new Date(s)).filter((d) => d && !isNaN(d)).map((d) => d.toISOString());

  const send = async () => {
    if (windows.length === 0) { toast.error(t("bk.cp_need_one")); return; }
    setBusy(true);
    try {
      await api.post(`/coach/reschedule-requests/${request.id}/counter-propose`, {
        counter_windows: windows,
        message: message.trim() || undefined,
      });
      toast.success(t("bk.cp_sent"));
      onSent();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bk-counter-dialog">
        <DialogHeader>
          <DialogTitle>{t("bk.cp_title")}</DialogTitle>
          <DialogDescription>{t("bk.cp_sub", { name: request.client_name })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {slots.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Input type="datetime-local" value={s} onChange={(e) => setSlot(i, e.target.value)} className="h-9" data-testid={`bk-cp-slot-${i}`} />
              {slots.length > 1 && (
                <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 rounded-full text-rose-500" onClick={() => removeSlot(i)}><X className="h-4 w-4" /></Button>
              )}
            </div>
          ))}
          {slots.length < 3 && (
            <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={addSlot} data-testid="bk-cp-add"><Plus className="h-3.5 w-3.5" /> {t("bk.cp_add_slot")}</Button>
          )}
        </div>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} placeholder={t("bk.cp_message_ph")} className="min-h-[70px]" data-testid="bk-cp-message" />
        <Button className="w-full gap-1.5 rounded-full" disabled={busy || windows.length === 0} onClick={send} data-testid="bk-cp-send">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("bk.cp_send")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
